
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';

interface AccountChange {
  id: string;
  merchantId: string;
  codigoComercio: string;
  severity: 'HIGH' | 'INFO';
  hasActiveAdvance: boolean;
  montoRiesgo: string;
  cuentaAnterior: Record<string, unknown> | null;
  cuentaNueva: Record<string, unknown>;
  resolution: string | null;
  createdAt: string;
}

interface OpenPaymentRequest {
  id: string;
  lupsRequestId: string | null;
  advanceRequestId: string;
  installmentDetailId: string;
  monto: string;
  fechaObjetivo: string;
  sendStatus: 'pending' | 'sent' | 'in_collection' | 'send_failed';
}

interface ListResponse {
  accountChange: AccountChange;
  paymentRequests: OpenPaymentRequest[];
}

interface DeleteRowResult {
  paymentRequestId: string;
  ok: boolean;
  httpStatus: number | null;
  errorCode: string | null;
}

interface DeleteBatchResponse {
  accountChangeHistoryId: string;
  results: DeleteRowResult[];
}

const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const formatFecha = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Santiago' }).format(new Date(`${iso}T00:00:00Z`));
  } catch {
    return iso;
  }
};

const formatCLP = (n: number | string) => Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

const STATUS_LABELS: Record<OpenPaymentRequest['sendStatus'], { label: string; className: string }> = {
  pending: { label: 'Pendiente de envío', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200' },
  sent: { label: 'Enviado a Lups', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200' },
  in_collection: { label: 'En cobro', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' },
  send_failed: { label: 'Falla de envío', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200' },
};

function formatAccountSnapshot(snapshot: Record<string, unknown> | null): string {
  if (!snapshot) return '(sin registro previo)';
  const banco = String(snapshot.banco ?? '');
  const tipo = String(snapshot.tipoCuenta ?? '');
  const numero = String(snapshot.numeroCuenta ?? '');
  const rut = String(snapshot.rutTitular ?? '');
  const parts = [banco, tipo, numero].filter(Boolean).join(' — ');
  return rut ? `${parts} (${rut})` : parts;
}

function AccountChangeDetailView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [lastResult, setLastResult] = useState<DeleteBatchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminHttpClient.get<ListResponse>(getAdminEndpointUrl('accountChanges', 'listPaymentRequests', { id }));
      setData(res);
      setSelected(new Set());
    } catch (err) {
      setError((err as Error).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleOne = (prId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(prId)) next.delete(prId);
      else next.add(prId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!data) return;
    setSelected((prev) => (prev.size === data.paymentRequests.length ? new Set() : new Set(data.paymentRequests.map((p) => p.id))));
  };

  const handleDelete = async () => {
    if (!id || selected.size === 0) return;
    const count = selected.size;
    if (!window.confirm(`Eliminar ${count} payment request${count === 1 ? '' : 's'} en Lups? Esta acción es best-effort y queda auditada.`)) return;

    setDeleting(true);
    setLastResult(null);
    setError(null);
    try {
      const res = await adminHttpClient.post<DeleteBatchResponse>(
        getAdminEndpointUrl('accountChanges', 'deletePaymentRequests', { id }),
        { paymentRequestIds: Array.from(selected), note: note.trim() || undefined },
      );
      setLastResult(res);
      setNote('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const summary = useMemo(() => {
    if (!lastResult) return null;
    const ok = lastResult.results.filter((r) => r.ok).length;
    const notFound = lastResult.results.filter((r) => r.httpStatus === 404).length;
    const alreadyPaid = lastResult.results.filter((r) => r.httpStatus === 409).length;
    const other = lastResult.results.length - ok - notFound - alreadyPaid;
    return { ok, notFound, alreadyPaid, other, total: lastResult.results.length };
  }, [lastResult]);

  if (!id) {
    return (
      <AdminLayout title="Cambio de cuenta" subtitle="Falta el ID del cambio en la URL">
        <div className="rounded-xl border bg-card p-6">
          <p className="text-sm text-muted-foreground">No se encontró el parámetro <code className="font-mono text-xs">?id=</code>.</p>
          <button type="button" onClick={() => navigate('/admin/alertas')} className="mt-4 text-sm text-flux-primary hover:underline">
            ← Volver a alertas
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Respaldo ante cambio de cuenta" subtitle="Eliminar payment requests en Lups como respaldo">
      <div className="space-y-6 max-w-6xl">
        <button type="button" onClick={() => navigate('/admin/alertas')} className="text-sm text-flux-primary hover:underline">
          ← Volver a alertas
        </button>

        {loading && <p className="text-sm text-muted-foreground">Cargando…</p>}

        {!loading && error && (
          <div className="rounded-xl border border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/30 p-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {!loading && data && (
          <>
            <section className="rounded-xl border bg-card p-6">
              <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold">Detalle del cambio</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Comercio <span className="font-mono">{data.accountChange.merchantId}</span> · código <span className="font-mono">{data.accountChange.codigoComercio}</span>
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${
                    data.accountChange.severity === 'HIGH'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                  }`}
                >
                  {data.accountChange.severity}
                </span>
              </header>

              <dl className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Detectado</dt>
                  <dd className="mt-0.5">{formatDate(data.accountChange.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Monto en riesgo</dt>
                  <dd className="mt-0.5 font-medium">{formatCLP(data.accountChange.montoRiesgo)}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Cuenta anterior</dt>
                  <dd className="mt-0.5 font-mono text-xs">{formatAccountSnapshot(data.accountChange.cuentaAnterior)}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Cuenta nueva</dt>
                  <dd className="mt-0.5 font-mono text-xs">{formatAccountSnapshot(data.accountChange.cuentaNueva)}</dd>
                </div>
                {data.accountChange.resolution && (
                  <div className="md:col-span-2">
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">Resolución registrada</dt>
                    <dd className="mt-0.5 whitespace-pre-wrap text-xs">{data.accountChange.resolution}</dd>
                  </div>
                )}
              </dl>
            </section>

            <section className="rounded-xl border bg-card p-6">
              <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold">Payment requests abiertos del comercio</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Selecciona los que quieras eliminar en Lups como respaldo. El cobro automatizado (PAT) ya no se ejecuta sobre la cuenta TBK,
                    pero puedes usar esta acción si detectas riesgo.
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium">{data.paymentRequests.length}</span>
              </header>

              {data.paymentRequests.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">El comercio no tiene payment requests abiertos. Nada que eliminar.</p>
              ) : (
                <>
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="py-2 pr-3 w-8">
                            <input
                              type="checkbox"
                              checked={selected.size === data.paymentRequests.length && data.paymentRequests.length > 0}
                              onChange={toggleAll}
                              className="h-4 w-4"
                              aria-label="Seleccionar todos"
                            />
                          </th>
                          <th className="py-2 pr-3">Adelanto / cuota</th>
                          <th className="py-2 pr-3">Monto</th>
                          <th className="py-2 pr-3">Fecha objetivo</th>
                          <th className="py-2 pr-3">Estado Lups</th>
                          <th className="py-2 pr-3">Lups UUID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.paymentRequests.map((pr) => {
                          const badge = STATUS_LABELS[pr.sendStatus];
                          const checked = selected.has(pr.id);
                          return (
                            <tr key={pr.id} className="border-b border-border/50 last:border-b-0">
                              <td className="py-3 pr-3">
                                <input type="checkbox" checked={checked} onChange={() => toggleOne(pr.id)} className="h-4 w-4" aria-label={`Seleccionar ${pr.id}`} />
                              </td>
                              <td className="py-3 pr-3">
                                <p className="font-mono text-xs">{pr.advanceRequestId}</p>
                                <p className="mt-0.5 font-mono text-[0.7rem] text-muted-foreground">cuota {pr.installmentDetailId.slice(0, 10)}…</p>
                              </td>
                              <td className="py-3 pr-3 tabular-nums">{formatCLP(pr.monto)}</td>
                              <td className="py-3 pr-3">{formatFecha(pr.fechaObjetivo)}</td>
                              <td className="py-3 pr-3">
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>{badge.label}</span>
                              </td>
                              <td className="py-3 pr-3">
                                {pr.lupsRequestId ? (
                                  <span className="font-mono text-[0.7rem]" title={pr.lupsRequestId}>
                                    {pr.lupsRequestId.slice(0, 8)}…
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground italic">sin uuid (no enviado)</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 space-y-3 border-t border-border pt-4">
                    <label className="block text-sm">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Nota (opcional)</span>
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        maxLength={2000}
                        placeholder="Ej. cliente confirmó cuenta nueva, cerramos cobros pendientes"
                        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                    </label>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-xs text-muted-foreground">
                        Seleccionados <strong>{selected.size}</strong> de {data.paymentRequests.length}
                      </p>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={deleting || selected.size === 0}
                        className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deleting ? 'Eliminando…' : `Eliminar ${selected.size} en Lups`}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>

            {lastResult && summary && (
              <section className="rounded-xl border bg-card p-6">
                <h3 className="text-base font-semibold">Resultado del último batch</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {summary.ok}/{summary.total} eliminados correctamente
                  {summary.notFound > 0 && <> · {summary.notFound} no encontrados en Lups</>}
                  {summary.alreadyPaid > 0 && <> · {summary.alreadyPaid} ya pagados (revisar)</>}
                  {summary.other > 0 && <> · {summary.other} con error</>}
                </p>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="py-2 pr-3">Payment request</th>
                        <th className="py-2 pr-3">Resultado</th>
                        <th className="py-2 pr-3">HTTP</th>
                        <th className="py-2 pr-3">Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastResult.results.map((r) => {
                        let badgeClass = 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
                        let label = 'Error';
                        if (r.ok) {
                          badgeClass = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
                          label = 'Eliminado';
                        } else if (r.httpStatus === 404) {
                          badgeClass = 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
                          label = 'No encontrado';
                        } else if (r.httpStatus === 409) {
                          badgeClass = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200';
                          label = 'Ya pagado';
                        } else {
                          badgeClass = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
                        }
                        return (
                          <tr key={r.paymentRequestId} className="border-b border-border/50 last:border-b-0">
                            <td className="py-2 pr-3 font-mono text-xs">{r.paymentRequestId}</td>
                            <td className="py-2 pr-3">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}>{label}</span>
                            </td>
                            <td className="py-2 pr-3 font-mono text-xs">{r.httpStatus ?? '—'}</td>
                            <td className="py-2 pr-3 text-xs text-muted-foreground truncate max-w-[40ch]" title={r.errorCode ?? ''}>
                              {r.errorCode ?? (r.ok ? 'OK' : '—')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default function AdminAccountChangeDetailPage() {
  return (
    <Suspense fallback={<AdminLayout title="Cambio de cuenta" subtitle="Cargando…"><p className="text-sm text-muted-foreground">Cargando…</p></AdminLayout>}>
      <AccountChangeDetailView />
    </Suspense>
  );
}
